import React from 'react';
import { FiGlobe, FiTrendingUp, FiCheckCircle, FiBarChart2, FiUsers, FiShield, FiClock, FiZap,  FiHome } from 'react-icons/fi';

const GroupInstitutions = () => {
  const challenges = [
    {
      icon: <FiHome className="w-6 h-6" />,
      title: 'Different Locations, Different Problems',
      description: 'Managing institutions across cities or states is a logistical challenge. Each location has its own local issues—different regulatory requirements, different labor markets, different parent expectations. What works in Bangalore might fail in Bihar.',
      solution: 'Truyon provides location-aware management. Each institution gets local flexibility while maintaining trust-wide standards.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Inconsistent Staff Quality and Training',
      description: 'Staff quality varies across institutions. One campus has experienced administrators, another has newly hired staff with minimal training. This inconsistency affects operations, parent satisfaction, and ultimately, student outcomes.',
      solution: 'Truyon standardizes training and processes. New staff get up to speed faster with consistent systems and workflows.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'No Standardized Reporting',
      description: 'Reports from different institutions don\'t match. One principal reports attendance differently from another. Finance data is structured differently across campuses. This makes consolidation a nightmare and decision-making unreliable.',
      solution: 'Truyon provides standardized reporting across all institutions. Compare apples to apples, not apples to oranges.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Time-Consuming Manual Consolidation',
      description: 'Someone—usually a senior administrator—spends hours or days manually consolidating reports from different institutions. Spreadsheets, emails, phone calls. It\'s a thankless, error-prone process that shouldn\'t exist in 2026.',
      solution: 'Truyon automatically consolidates data from all institutions. Reports are generated in seconds, not days.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Security and Compliance Risks',
      description: 'Each institution managing its own data creates security vulnerabilities. Some campuses use strong security practices, others don\'t. Data breaches become a real risk across the group. Compliance with regulations becomes impossible to track centrally.',
      solution: 'Truyon provides enterprise-grade security across all institutions. One set of security standards, enforced everywhere.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'Difficulty Scaling New Campuses',
      description: 'Opening a new institution should be exciting, but it often becomes a logistical nightmare. Different systems, different processes, different training. The administrative burden of launching a new campus outweighs the excitement of growth.',
      solution: 'Truyon makes opening new campuses simple. Add a new institution in minutes, not months. All processes, templates, and workflows are ready to go.',
      color: 'bg-blue-50'
    }
  ];

  const solutions = [
    {
      title: 'One System, All Campuses',
      description: 'Instead of every institution having its own systems and processes, Truyon provides one unified system for all campuses. Consistent processes, consistent data, consistent quality.',
      icon: <FiGlobe className="w-5 h-5" />
    },
    {
      title: 'Centralized Administration',
      description: 'Move repetitive administrative tasks from each institution to a central team. Standardize admissions, fee collection, HR, and reporting across the group.',
      icon: <FiZap className="w-5 h-5" />
    },
    {
      title: 'Real-Time Visibility',
      description: 'No more waiting for end-of-month reports. See what\'s happening in every institution in real-time. Attendance, admissions, finances, staff performance—all at your fingertips.',
      icon: <FiBarChart2 className="w-5 h-5" />
    },
    {
      title: 'Consistent Brand Experience',
      description: 'Every parent, student, and staff member should experience the same quality across all your institutions. Truyon helps maintain consistent standards everywhere.',
      icon: <FiShield className="w-5 h-5" />
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Running a group of educational institutions is like conducting an orchestra. Each institution is a different instrument, playing a different part, in a different section. The challenge isn't just making each one sound good—it's making them all work together to create something beautiful.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        But in reality, most educational groups are not orchestras. They're collections of independent institutions, each doing its own thing, each using its own systems, each operating in its own bubble. <strong className="text-slate-900">The result is chaos, inefficiency, and missed opportunities.</strong>
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here are the real challenges of managing a group of institutions—and how Truyon solves them.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">40%</div>
          <div className="text-sm text-slate-500">Wasted time on manual consolidation</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">70%</div>
          <div className="text-sm text-slate-500">Groups struggle with inconsistent processes</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-rose-600">3x</div>
          <div className="text-sm text-slate-500">Faster expansion with unified systems</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">The Real Challenges of Managing a Group</h2>

      {/* Challenges List */}
      {challenges.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-1">
                  <FiCheckCircle className="w-4 h-4" />
                  <span>Truyon's Solution</span>
                </div>
                <p className="text-sm text-slate-700">{item.solution}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* The Big Picture */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">The Big Picture: What Happens When You Solve These Challenges</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {solutions.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200 flex items-start gap-3">
              <div className="bg-white rounded-lg p-2 flex-shrink-0">
                {item.icon}
              </div>
              <div>
                <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                <p className="text-xs text-slate-600">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">The Difference: Fragmented vs. Unified</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">The Fragmented Reality</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Every institution has its own systems</span></li>
              <li className="flex items-start gap-2">• <span>Data scattered across platforms</span></li>
              <li className="flex items-start gap-2">• <span>No central visibility</span></li>
              <li className="flex items-start gap-2">• <span>Inconsistent processes everywhere</span></li>
              <li className="flex items-start gap-2">• <span>Staff training is duplicated across campuses</span></li>
              <li className="flex items-start gap-2">• <span>Opening new campuses is a major project</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">The Unified Truyon Approach</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>One platform for all institutions</span></li>
              <li className="flex items-start gap-2">• <span>All data in one place</span></li>
              <li className="flex items-start gap-2">• <span>Complete visibility from a central dashboard</span></li>
              <li className="flex items-start gap-2">• <span>Consistent processes with local flexibility</span></li>
              <li className="flex items-start gap-2">• <span>Single training program for all staff</span></li>
              <li className="flex items-start gap-2">• <span>Add new campuses in minutes</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* The Human Story */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Human Cost of Managing a Group the Hard Way</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          Behind the challenges of managing a group are real people. Trustees who want to focus on strategy but are buried in operational details. Principals who feel isolated, unable to learn from sister institutions. Administrators who are exhausted from reconciling inconsistent reports.
        </p>
        <p className="text-amber-800 leading-relaxed mb-3">
          Students and parents who experience inconsistent quality across campuses. Staff who are frustrated by learning different systems for different institutions. Leadership teams who make decisions based on incomplete or incorrect data.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>Managing a group of institutions shouldn't be this hard.</strong> With the right systems, it can be simple, efficient, and even enjoyable.
        </p>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">From Chaos to Control</h3>
        <p className="text-indigo-800 leading-relaxed">
          Managing a group of institutions is one of the most complex challenges in education. Different locations, different staff, different systems, different challenges. But complexity doesn't have to mean chaos.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          With the right platform, you can move from managing complexity to mastering it. One system. Consistent processes. Complete visibility. Real-time insights. The ability to scale effortlessly.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon gives you that control. <strong>Your group is diverse. Your ERP should be unified.</strong>
        </p>
      </div>
    </>
  );
};

export default GroupInstitutions;